import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, InputAdornment, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import VisibilityActionButton from '../../../../components/designe/ActionButtons/VisibilityActionButton';
import {
  AppButton,
  AppCancelButton,
  AppSaveButton,
} from '../../../../components/designe/buttons/app-button/AppButton';
import AppFormTextField from '../../../../components/designe/forms/app-form-text-field';
import { useStyles } from './LinkedAccounts.styles';
import { useLinkedAccountsComponent } from './useLinkedAccountsComponent';

const PROVIDER_KEYS: Record<string, string> = {
  password: 'providerEmail',
  'google.com': 'providerGoogle',
  'apple.com': 'providerApple',
  'facebook.com': 'providerFacebook',
};

export function LinkedAccounts() {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    loading,
    showLinkEmailForm,
    setShowLinkEmailForm,
    showChangePasswordForm,
    setShowChangePasswordForm,
    linkEmailFormik,
    changePasswordFormik,
    handleLinkGoogle,
    handleLinkApple,
    handleLinkFacebook,
    isPasswordLinked,
    isGoogleLinked,
    isAppleLinked,
    isFacebookLinked,
    appleAuthorizationActive,
    facebookAuthorizationActive,
    userEmail,
    passwordProvider,
    googleProvider,
    appleProviderInfo,
    facebookProviderInfo,
  } = useLinkedAccountsComponent();

  if (loading) {
    return (
      <div className={classes.wrapper}>
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </div>
    );
  }

  const passwordProps = {
    type: showPassword ? 'text' : ('password' as const),
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <VisibilityActionButton
            isActive={showPassword}
            onClick={() => setShowPassword(!showPassword)}
          />
        </InputAdornment>
      ),
    },
  };

  return (
    <div className={classes.wrapper}>
      {/* Email / password */}
      <div className={classes.providerRow}>
        <Box className={classes.providerColumn}>
          <Typography variant="text-md">
            {t(`settings.${PROVIDER_KEYS['password']}`)}
          </Typography>
          {passwordProvider?.email && (
            <Typography variant="text-sm" className={classes.providerSecondary}>
              {passwordProvider.email}
            </Typography>
          )}
        </Box>
        {isPasswordLinked ? (
          <AppButton
            type="button"
            size="small"
            variant="outlined"
            onClick={() => {
              setShowChangePasswordForm((v) => !v);
              setShowLinkEmailForm(false);
            }}>
            {t('settings.changePassword')}
          </AppButton>
        ) : (
          <AppButton
            type="button"
            size="small"
            onClick={() => {
              setShowLinkEmailForm((v) => !v);
              setShowChangePasswordForm(false);
            }}>
            {t('settings.linkProvider')}
          </AppButton>
        )}
      </div>
      {!isPasswordLinked && showLinkEmailForm && userEmail && (
        <div className={classes.formSection}>
          <Typography variant="text-sm" sx={{ mb: 2 }}>
            {t('settings.linkEmailDescription')}
          </Typography>
          <form onSubmit={linkEmailFormik.handleSubmit}>
            <Box>
              <AppFormTextField
                label={t('settings.password')}
                fullWidth
                name="password"
                formik={linkEmailFormik}
                errorIconRightPadding={40}
                {...passwordProps}
              />
            </Box>
            <Box marginTop={2}>
              <AppFormTextField
                label={t('settings.confirmPassword')}
                fullWidth
                name="confirmPassword"
                formik={linkEmailFormik}
                errorIconRightPadding={40}
                {...passwordProps}
              />
            </Box>
            <div className={classes.buttonWrapper}>
              <AppButton
                type="button"
                variant="outlined"
                onClick={() => setShowLinkEmailForm(false)}>
                {t('common.cancel')}
              </AppButton>
              <AppButton type="submit" disabled={!linkEmailFormik.isValid}>
                {t('settings.linkProvider')}
              </AppButton>
            </div>
          </form>
        </div>
      )}
      {isPasswordLinked && showChangePasswordForm && (
        <div className={classes.formSection}>
          <form onSubmit={changePasswordFormik.handleSubmit}>
            <Box>
              <AppFormTextField
                label={t('settings.currentPassword')}
                fullWidth
                name="currentPassword"
                formik={changePasswordFormik}
                errorIconRightPadding={40}
                {...passwordProps}
              />
            </Box>
            <Box marginTop={2}>
              <AppFormTextField
                label={t('settings.password')}
                fullWidth
                name="password"
                formik={changePasswordFormik}
                errorIconRightPadding={40}
                {...passwordProps}
              />
            </Box>
            <Box marginTop={2}>
              <AppFormTextField
                label={t('settings.confirmPassword')}
                fullWidth
                name="confirmPassword"
                formik={changePasswordFormik}
                errorIconRightPadding={40}
                {...passwordProps}
              />
            </Box>
            <div className={classes.buttonWrapper}>
              <AppCancelButton
                type="button"
                onClick={() => setShowChangePasswordForm(false)}>
                {t('common.cancel')}
              </AppCancelButton>
              <AppSaveButton
                type="submit"
                disabled={!changePasswordFormik.isValid}>
                {t('settings.changePassword')}
              </AppSaveButton>
            </div>
          </form>
        </div>
      )}

      {/* Google */}
      <div className={classes.providerRow}>
        <Box className={classes.providerColumn}>
          <Typography variant="text-md">
            {t('settings.providerGoogle')}
          </Typography>
          {isGoogleLinked &&
            (googleProvider?.email || googleProvider?.displayName) && (
              <>
                {googleProvider?.displayName && (
                  <Typography variant="text-sm">
                    {googleProvider.displayName}
                  </Typography>
                )}
                {googleProvider?.email && (
                  <Typography
                    variant="text-sm"
                    className={classes.providerSecondary}>
                    {googleProvider.email}
                  </Typography>
                )}
              </>
            )}
        </Box>
        {isGoogleLinked ? (
          <Typography variant="text-sm" className={classes.providerSecondary}>
            {t('settings.linked')}
          </Typography>
        ) : (
          <AppButton type="button" size="small" onClick={handleLinkGoogle}>
            {t('settings.linkProvider')}
          </AppButton>
        )}
      </div>

      {/* Facebook */}
      {facebookAuthorizationActive && (
        <div className={classes.providerRow}>
          <Box className={classes.providerColumn}>
            <Typography variant="text-md">
              {t('settings.providerFacebook')}
            </Typography>
            {isFacebookLinked &&
              (facebookProviderInfo?.email ||
                facebookProviderInfo?.displayName) && (
                <>
                  {facebookProviderInfo?.displayName && (
                    <Typography variant="text-sm">
                      {facebookProviderInfo.displayName}
                    </Typography>
                  )}
                  {facebookProviderInfo?.email && (
                    <Typography
                      variant="text-sm"
                      className={classes.providerSecondary}>
                      {facebookProviderInfo.email}
                    </Typography>
                  )}
                </>
              )}
          </Box>
          {isFacebookLinked ? (
            <Typography variant="text-sm" className={classes.providerSecondary}>
              {t('settings.linked')}
            </Typography>
          ) : (
            <AppButton type="button" size="small" onClick={handleLinkFacebook}>
              {t('settings.linkProvider')}
            </AppButton>
          )}
        </div>
      )}

      {/* Apple */}
      {appleAuthorizationActive && (
        <div className={classes.providerRow}>
          <Box className={classes.providerColumn}>
            <Typography className={classes.providerName} variant="text-md">
              {t('settings.providerApple')}
            </Typography>
            {(appleProviderInfo?.email || appleProviderInfo?.displayName) && (
              <>
                {appleProviderInfo?.displayName && (
                  <Typography variant="text-sm">
                    {appleProviderInfo.displayName}
                  </Typography>
                )}
                {appleProviderInfo?.email && (
                  <Typography
                    variant="text-sm"
                    className={classes.providerSecondary}>
                    {appleProviderInfo.email}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {isAppleLinked ? (
            <Typography variant="text-sm" className={classes.providerSecondary}>
              {t('settings.linked')}
            </Typography>
          ) : (
            <AppButton type="button" size="small" onClick={handleLinkApple}>
              {t('settings.linkProvider')}
            </AppButton>
          )}
        </div>
      )}
    </div>
  );
}
